/**
 * QueryChatPage — standalone chat powered by a Query Manager query.
 *
 * - Routes: /chatbot/{queryId}. Use "+ New Query" in LLM Manager, then open /chatbot/your-query-id.
 * - Send: llmProviderService.callLLMWithQuery(queryId, userInput). Backend returns output + sourcesUsed (RAG docs).
 *
 * Clinical references & sources:
 * - We load refs for this query via getCachedOrLoadReferencesForQuery (query-scoped + global clinical refs).
 * - Per assistant message: user + reply terms (filtered) → findReferencesByTitle + dual findMatchingReferences
 *   (user-weighted vs reply). Results merge then rankAndLimitSources caps the list for display.
 * - Inline source refs in prose are stripped; only the "Source documents" list at the bottom is shown.
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { MenuBook, Close as CloseIcon, OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { llmProviderService } from "../../services/llm";
import { getCachedOrLoadReferencesForQuery, findMatchingReferences, findReferencesByTitle } from "../../services/llm/clinicalReferenceService";
import ReferenceLink from "../../features/chartmind/components/chartmind/ReferenceLink";
import { AiAssistantDisclaimer } from "@healthdesk/shared-components";
import {
  extractContent,
  extractCitedSourceNames,
  parseDocumentTitle,
  parseStepActionResponse,
  sanitizeMarkdownChildren,
} from "./chatbotMessageUtils";
import { referenceSourceDisplayLabel } from "../../utils/parseDocumentTitle";

// ---- Clinical reference matching (chatbot) ----
const MIN_TERM_LENGTH = 3;
const MAX_TERMS_PER_MESSAGE = 30;
const CONTENT_SIMILARITY_THRESHOLD = 0.4;
/** Min weighted sum from user (×2) + reply (×1) tag-term hits for a ref to enter the content set */
const MIN_WEIGHTED_TAG_MATCHES_FOR_CONTENT = 2;
const USER_TAG_MATCH_WEIGHT = 2;
const REPLY_TAG_MATCH_WEIGHT = 1;
/** Hard cap on "Source documents" links per assistant message (cited sources compete for same slots). */
const MAX_DISPLAYED_SOURCE_DOCUMENTS = 4;

/** Empty-state copy for Ask Me Anything (ama-kijabe-1) only; other query chats use a generic prompt. */
const AMA_KIJABE_EMPTY_STATE_MESSAGE =
  "AIC Kijabe Hospital educational resources will be used to generate responses.";

/** Excluded from matching: only clinical terms may drive refs. Blocklist = generic + question/stop words (lowercase). */
const GENERIC_TERM_BLOCKLIST = new Set([
  "management", "assessment", "assessments", "guidelines", "guideline", "protocols", "protocol",
  "referral", "referrals", "overview", "introduction", "document", "documents", "information",
  "general", "section", "sections", "recommendations", "recommendation",
  "what", "about", "when", "where", "which", "this", "that", "with", "from", "have", "been", "they",
  "there", "would", "could", "should", "will", "your", "please", "need", "want", "know", "think",
  "like", "some", "more", "other", "into", "only", "just", "also", "such", "than", "then", "them",
  "these", "those", "after", "before", "being", "both", "does", "during", "each", "here", "make",
  "many", "most", "must", "over", "same", "take", "were", "while", "their", "said",
  "patient", "patients", "clinical", "medicine", "medical", "health", "treatment", "treatments",
]);

/** Remove generic (non-clinical) terms so only clinical vocabulary drives reference matching. */
function filterToClinicalTerms(terms) {
  if (!terms?.length) return [];
  return terms.filter((t) => !GENERIC_TERM_BLOCKLIST.has(String(t).toLowerCase().trim()));
}

/**
 * Extract candidate clinical terms from assistant reply text for reference matching.
 * Uses simple heuristics: words of sufficient length, deduped, limited count.
 */
function extractTermsFromText(text) {
  if (!text || typeof text !== "string") return [];
  const words = text
    .replace(/[\n\r]+/g, " ")
    .split(/\s+/)
    .map((w) => w.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, ""))
    .filter((w) => w.length >= MIN_TERM_LENGTH);
  const seen = new Set();
  const terms = [];
  for (const w of words) {
    const lower = w.toLowerCase();
    if (!seen.has(lower)) {
      seen.add(lower);
      terms.push(w);
    }
    if (terms.length >= MAX_TERMS_PER_MESSAGE) break;
  }
  return terms;
}

/** Word-boundary check: needle appears in haystack at a word boundary (for title matching). Includes _ so "stroke" matches in "11052025_Stroke.pdf". */
function isWordBoundaryIn(haystack, needle) {
  if (!haystack || !needle || needle.length < 3) return false;
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  const boundary = /[\s\-_/(),;:.]/;
  let idx = h.indexOf(n);
  while (idx !== -1) {
    const before = idx === 0 ? "" : h[idx - 1];
    const after = idx + n.length >= h.length ? "" : h[idx + n.length];
    if (boundary.test(before) || before === "") {
      if (boundary.test(after) || after === "") return true;
    }
    idx = h.indexOf(n, idx + 1);
  }
  return false;
}

/** Normalize embedded source to ReferenceLink shape (parsed title, no matchedTag). */
function embeddedToRef(s) {
  const displayTitle = parseDocumentTitle(s.documentName) || "Document";
  return {
    id: s.downloadURL,
    source: displayTitle,
    downloadURL: s.downloadURL,
    fileName: s.documentName,
    matchedTag: undefined,
  };
}

/** Max tag matchScore per document URL from a term → refs match map. */
function maxMatchScoreByUrl(matchMap) {
  const byUrl = new Map();
  if (!matchMap) return byUrl;
  for (const refs of Object.values(matchMap)) {
    if (!Array.isArray(refs)) continue;
    for (const ref of refs) {
      if (!ref?.downloadURL) continue;
      const sc = ref.matchScore || 0;
      const prev = byUrl.get(ref.downloadURL) || 0;
      if (sc > prev) byUrl.set(ref.downloadURL, sc);
    }
  }
  return byUrl;
}

/** Max RAG similarity per URL from embedded sources list. */
function maxSimilarityByUrl(embeddedSources) {
  const byUrl = new Map();
  for (const s of embeddedSources || []) {
    if (!s?.downloadURL || typeof s.similarity !== "number") continue;
    const prev = byUrl.get(s.downloadURL) || 0;
    if (s.similarity > prev) byUrl.set(s.downloadURL, s.similarity);
  }
  return byUrl;
}

/**
 * Per term bucket: at most one weighted increment per ref id (same as legacy single-map behavior).
 * User and reply maps use different weights so one strong user hit can pass the threshold without reply support.
 */
function accumulateWeightedMatchMaps(matchMapUser, matchMapReply) {
  const refIdToWeighted = new Map();
  const refIdToRef = new Map();

  const consume = (matchMap, weight) => {
    if (!matchMap) return;
    for (const refs of Object.values(matchMap)) {
      if (!Array.isArray(refs)) continue;
      const seenThisTerm = new Set();
      for (const ref of refs) {
        if (!ref?.id) continue;
        if (!seenThisTerm.has(ref.id)) {
          seenThisTerm.add(ref.id);
          refIdToWeighted.set(ref.id, (refIdToWeighted.get(ref.id) || 0) + weight);
        }
        const existing = refIdToRef.get(ref.id);
        if (!existing || (ref.matchScore || 0) > (existing.matchScore || 0)) {
          refIdToRef.set(ref.id, ref);
        }
      }
    }
  };

  consume(matchMapUser, USER_TAG_MATCH_WEIGHT);
  consume(matchMapReply, REPLY_TAG_MATCH_WEIGHT);
  return { refIdToWeighted, refIdToRef };
}

/**
 * Rank by cited > user-title match > user tag score > RAG similarity > reply tag score > label; cap list length.
 * If more than MAX_DISPLAYED_SOURCE_DOCUMENTS are cited, only the first slots after sort are shown (consistent cap).
 */
function rankAndLimitSources(allRefs, ctx) {
  const {
    refMatchesCited,
    urlsFromUserTitle,
    userTagScoreByUrl,
    replyTagScoreByUrl,
    urlToSimilarity,
  } = ctx;
  if (!allRefs?.length) return [];

  const sorted = [...allRefs].sort((a, b) => {
    const ca = refMatchesCited(a) ? 1 : 0;
    const cb = refMatchesCited(b) ? 1 : 0;
    if (cb !== ca) return cb - ca;
    const ua = urlsFromUserTitle.has(a.downloadURL) ? 1 : 0;
    const ub = urlsFromUserTitle.has(b.downloadURL) ? 1 : 0;
    if (ub !== ua) return ub - ua;
    const uta = userTagScoreByUrl.get(a.downloadURL) || 0;
    const utb = userTagScoreByUrl.get(b.downloadURL) || 0;
    if (utb !== uta) return utb - uta;
    const ra = urlToSimilarity.get(a.downloadURL) || 0;
    const rb = urlToSimilarity.get(b.downloadURL) || 0;
    if (rb !== ra) return rb - ra;
    const rta = replyTagScoreByUrl.get(a.downloadURL) || 0;
    const rtb = replyTagScoreByUrl.get(b.downloadURL) || 0;
    if (rtb !== rta) return rtb - rta;
    return referenceSourceDisplayLabel(a).localeCompare(referenceSourceDisplayLabel(b), undefined, {
      sensitivity: "base",
    });
  });

  return sorted.slice(0, MAX_DISPLAYED_SOURCE_DOCUMENTS);
}

/**
 * Build a single deduped list of source documents for display under an assistant message.
 * Combines: (1) clinical refs + embedded docs matching query/reply by title, (2) clinical refs matching by tags
 * (user- and reply-weighted match maps), (3) embedded docs above similarity threshold, (4) fallback embedded.
 * Final list is ranked and capped at MAX_DISPLAYED_SOURCE_DOCUMENTS.
 */
function splitSourcesForDisplay(
  userQuery,
  replyContent,
  embeddedSources,
  citedSourceNames,
  matchMapUser,
  matchMapReply,
  allReferences
) {
  const normalizeForCiteMatch = (name) =>
    (name || "").toLowerCase().replace(/\.(pdf|docx?|txt|md)$/i, "").trim();
  const citedSet = new Set((citedSourceNames || []).map(normalizeForCiteMatch).filter(Boolean));
  const refMatchesCited = (ref) => {
    if (citedSet.size === 0) return false;
    const refNorm = normalizeForCiteMatch(
      referenceSourceDisplayLabel(ref) || ref.fileName || ref.documentName || ""
    );
    if (!refNorm) return false;
    for (const c of citedSet) {
      if (refNorm === c || refNorm.includes(c) || c.includes(refNorm)) return true;
    }
    return false;
  };

  const userTitleTermsDeduped = filterToClinicalTerms([
    ...new Set(
      extractTermsFromText(userQuery || "")
        .map((t) => t.toLowerCase().trim())
        .filter((t) => t.length >= MIN_TERM_LENGTH)
    ),
  ]);

  const titleTerms = [
    ...extractTermsFromText(userQuery || ""),
    ...extractTermsFromText(replyContent || ""),
  ]
    .map((t) => t.toLowerCase().trim())
    .filter((t) => t.length >= MIN_TERM_LENGTH);
  const titleTermsDeduped = filterToClinicalTerms([...new Set(titleTerms)]);

  const urlsFromUserTitle = new Set();
  findReferencesByTitle(userTitleTermsDeduped, allReferences || []).forEach((r) => {
    if (r.downloadURL) urlsFromUserTitle.add(r.downloadURL);
  });
  (embeddedSources || []).forEach((s) => {
    if (
      s.downloadURL &&
      userTitleTermsDeduped.some((term) => isWordBoundaryIn(s.documentName || "", term))
    ) {
      urlsFromUserTitle.add(s.downloadURL);
    }
  });

  const userTagScoreByUrl = maxMatchScoreByUrl(matchMapUser);
  const replyTagScoreByUrl = maxMatchScoreByUrl(matchMapReply);
  const urlToSimilarity = maxSimilarityByUrl(embeddedSources);

  // Title set: clinical by title + embedded by title
  const clinicalByTitle = findReferencesByTitle(titleTermsDeduped, allReferences || []);
  const embeddedByTitle = (embeddedSources || []).filter((s) =>
    titleTermsDeduped.some((term) => isWordBoundaryIn(s.documentName || "", term))
  );

  const byUrl = new Map();
  const addToMap = (ref, url) => {
    if (!url || byUrl.has(url)) return;
    const displayTitle = referenceSourceDisplayLabel(ref) || "Document";
    byUrl.set(url, {
      id: ref.id || url,
      source: displayTitle,
      downloadURL: url,
      fileName: ref.fileName ?? ref.documentName,
      matchedTag: undefined,
    });
  };
  clinicalByTitle.forEach((r) => addToMap(r, r.downloadURL));
  embeddedByTitle.forEach((s) => addToMap(embeddedToRef(s), s.downloadURL));
  const titleRefs = Array.from(byUrl.values());

  const titleUrls = new Set(titleRefs.map((r) => r.downloadURL));

  const { refIdToWeighted, refIdToRef } = accumulateWeightedMatchMaps(matchMapUser, matchMapReply);
  const contentClinicalDeduped = [];
  for (const [id, weighted] of refIdToWeighted) {
    if (weighted < MIN_WEIGHTED_TAG_MATCHES_FOR_CONTENT) continue;
    const ref = refIdToRef.get(id);
    if (ref?.downloadURL && !titleUrls.has(ref.downloadURL)) contentClinicalDeduped.push(ref);
  }

  const contentEmbedded = (embeddedSources || []).filter(
    (s) =>
      s.downloadURL &&
      !titleUrls.has(s.downloadURL) &&
      typeof s.similarity === "number" &&
      s.similarity >= CONTENT_SIMILARITY_THRESHOLD
  );

  const contentByUrl = new Map();
  contentClinicalDeduped.forEach((r) => addToMap(r, r.downloadURL));
  contentEmbedded.forEach((s) => addToMap(embeddedToRef(s), s.downloadURL));
  const contentRefs = Array.from(contentByUrl.values());

  const seenUrls = new Set([...titleRefs.map((r) => r.downloadURL), ...contentRefs.map((r) => r.downloadURL)]);
  const fallbackEmbedded = (embeddedSources || []).filter(
    (s) => s.downloadURL && s.documentName && !seenUrls.has(s.downloadURL)
  );
  const fallbackRefs = fallbackEmbedded.map((s) => embeddedToRef(s));

  const seenUrlsAll = new Set();
  const allRefs = [];
  for (const r of [...titleRefs, ...contentRefs, ...fallbackRefs]) {
    if (r.downloadURL && !seenUrlsAll.has(r.downloadURL)) {
      seenUrlsAll.add(r.downloadURL);
      allRefs.push(r);
    }
  }

  const limited = rankAndLimitSources(allRefs, {
    refMatchesCited,
    urlsFromUserTitle,
    userTagScoreByUrl,
    replyTagScoreByUrl,
    urlToSimilarity,
  });
  return { allRefs: limited };
}

/** Approximate height of one line of text for "7 lines" layout reduction (px). */
const LINE_HEIGHT_PX = 24;

/** Chat message `content` is usually a string; coerce so downstream text/markdown never receives a plain object. */
function messageContentToString(content) {
  if (content == null) return "";
  return typeof content === "string" ? content : JSON.stringify(content);
}

/**
 * @param {Object} props
 * @param {string} [props.overrideQueryId] - When set (e.g. for /ask-me-anything-2), use this query ID instead of the URL param.
 * @param {boolean} [props.compactLayout] - When true, hide query title and doc-settings alert and reduce text-entry area height (e.g. ask-me-anything-2).
 */
const QueryChatPage = ({ overrideQueryId, compactLayout = false } = {}) => {
  const { queryId: queryIdFromParams } = useParams();
  const queryId = overrideQueryId ?? queryIdFromParams;
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allReferences, setAllReferences] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [pdfViewer, setPdfViewer] = useState({ open: false, url: null, title: null });
  const [publicRemaining, setPublicRemaining] = useState(null);
  const listRef = useRef(null);

  const fetchPublicUsage = useCallback(() => {
    if (!compactLayout) return;
    llmProviderService.fetchAma2PublicUsage().then(({ remaining }) => setPublicRemaining(remaining)).catch(() => setPublicRemaining(null));
  }, [compactLayout]);

  const handleOpenPdfRef = (ref) => {
    if (ref?.downloadURL) {
      setPdfViewer({
        open: true,
        url: ref.downloadURL,
        title: referenceSourceDisplayLabel(ref) || "Document",
      });
    }
  };
  const handleClosePdfViewer = () => {
    setPdfViewer({ open: false, url: null, title: null });
  };

  /** MUI-backed components for markdown so **bold** and headers render properly in the chat bubble. */
  const chatMarkdownComponents = {
    p: ({ children }) => (
      <Typography variant="body2" component="p" sx={{ mb: 0.75, "&:last-child": { mb: 0 } }}>
        {sanitizeMarkdownChildren(children)}
      </Typography>
    ),
    strong: ({ children }) => (
      <Box component="strong" sx={{ fontWeight: 700 }}>
        {sanitizeMarkdownChildren(children)}
      </Box>
    ),
    h1: ({ children }) => (
      <Typography variant="subtitle1" component="h1" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }}>
        {sanitizeMarkdownChildren(children)}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, mt: 1.25, mb: 0.5 }}>
        {sanitizeMarkdownChildren(children)}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography variant="body2" component="h3" sx={{ fontWeight: 700, mt: 1, mb: 0.25 }}>
        {sanitizeMarkdownChildren(children)}
      </Typography>
    ),
    ul: ({ children }) => (
      <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 0.75 }}>
        {sanitizeMarkdownChildren(children)}
      </Box>
    ),
    ol: ({ children }) => (
      <Box component="ol" sx={{ m: 0, pl: 2.5, mb: 0.75 }}>
        {sanitizeMarkdownChildren(children)}
      </Box>
    ),
    li: ({ children }) => (
      <Typography variant="body2" component="li" sx={{ mb: 0.25 }}>
        {sanitizeMarkdownChildren(children)}
      </Typography>
    ),
  };

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Load clinical references for this query (Clinical References tab refs + global); cached when possible
  useEffect(() => {
    if (!queryId) return;
    let cancelled = false;
    setSourcesLoading(true);
    getCachedOrLoadReferencesForQuery(queryId)
      .then((refs) => {
        if (!cancelled) setAllReferences(refs || []);
      })
      .catch((err) => {
        if (!cancelled) console.warn("[QueryChatPage] Clinical references fetch failed:", err.message);
      })
      .finally(() => {
        if (!cancelled) setSourcesLoading(false);
      });
    return () => { cancelled = true; };
  }, [queryId]);

  useEffect(() => {
    if (compactLayout) fetchPublicUsage();
  }, [compactLayout, fetchPublicUsage]);

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || loading || !queryId) return;

    setInputValue("");
    setError(null);
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setLoading(true);
    if (compactLayout) {
      setPublicRemaining((prev) => (prev !== null && prev > 0 ? prev - 1 : prev));
    }

    try {
      const response = await llmProviderService.callLLMWithQuery(
        queryId,
        text,
        {
          featureName: `chatbot-${queryId}`,
          module: "query-chat",
          ...(compactLayout && { allowPublicDemo: true }),
        }
      );
      const content = extractContent(response?.output);
      const sourcesUsed = Array.isArray(response?.sourcesUsed) ? response.sourcesUsed : [];
      const citedSourceNames = extractCitedSourceNames(response?.output) || [];
      const documentUsageMode = response?.documentUsageMode ?? undefined;
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: content || "(No response)",
          sourcesUsed: sourcesUsed.length > 0 ? sourcesUsed : undefined,
          citedSourceNames: citedSourceNames.length > 0 ? citedSourceNames : undefined,
          documentUsageMode,
        },
      ]);
      if (compactLayout) fetchPublicUsage();
    } catch (err) {
      console.warn("Query chat request failed:", err);
      setError(err.message || "Request failed. Please try again.");
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Error: ${err.message || "Request failed."}`,
          isError: true,
        },
      ]);
      if (compactLayout) fetchPublicUsage();
    } finally {
      setLoading(false);
    }
  };

  if (!queryId) {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", p: 3 }}>
        <Alert severity="info">
          No chatbot specified. Create a query in LLM Manager, then open{" "}
          <strong>/chatbot/your-query-id</strong> (e.g. /chatbot/my-chatbot).
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 720,
        mx: "auto",
        height: compactLayout ? `calc(100vh - 48px - ${7 * LINE_HEIGHT_PX}px)` : "calc(100vh - 48px)",
        display: "flex",
        flexDirection: "column",
        py: compactLayout ? 1 : 2,
      }}
    >
      {!compactLayout && (
        <Typography variant="h6" sx={{ mb: 1, px: 1 }}>
          {queryId}
        </Typography>
      )}

      {!compactLayout && (
        <Alert severity="info" sx={{ mb: 1 }} icon={false}>
          <Typography variant="caption">
            To use your <strong>embedded documents</strong> in replies, enable Document Settings for this query in LLM Manager: open this query → <strong>Document Settings</strong> tab → turn on &quot;Use This Vector Store Exclusively&quot; or &quot;Supplement authoritative resources&quot; → Save. Clinical references below are matched by primary topics.
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Paper
        ref={listRef}
        variant="outlined"
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {messages.length === 0 && !loading && (
          <Typography color="text.secondary" variant="body2">
            {queryId === "ama-kijabe-1"
              ? AMA_KIJABE_EMPTY_STATE_MESSAGE
              : "Send a message to start."}
          </Typography>
        )}
        {messages.map((msg, i) => {
          const isAssistant = msg.role === "assistant";
          const userQuery =
            i > 0 && messages[i - 1].role === "user" ? messageContentToString(messages[i - 1].content) : "";
          const assistantText = isAssistant ? messageContentToString(msg.content) : "";
          const userTerms = isAssistant ? filterToClinicalTerms(extractTermsFromText(userQuery)) : [];
          const replyTerms =
            isAssistant && assistantText ? filterToClinicalTerms(extractTermsFromText(assistantText)) : [];
          const matchMapUser =
            isAssistant && userTerms.length > 0 && allReferences.length > 0
              ? findMatchingReferences(userTerms, allReferences)
              : {};
          const matchMapReply =
            isAssistant && replyTerms.length > 0 && allReferences.length > 0
              ? findMatchingReferences(replyTerms, allReferences)
              : {};
          const { allRefs } = isAssistant
            ? splitSourcesForDisplay(
                userQuery,
                assistantText,
                msg.sourcesUsed ?? [],
                msg.citedSourceNames ?? [],
                matchMapUser,
                matchMapReply,
                allReferences
              )
            : { allRefs: [] };

          return (
            <Box
              key={i}
              sx={{
                alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                maxWidth: "85%",
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor:
                  msg.role === "user"
                    ? "primary.main"
                    : msg.isError
                      ? "error.light"
                      : "grey.200",
                color: msg.role === "user" ? "primary.contrastText" : "text.primary",
              }}
            >
              {!isAssistant ? (
                <Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {messageContentToString(msg.content)}
                </Typography>
              ) : (() => {
                const steps = parseStepActionResponse(assistantText);
                if (steps && steps.length > 0) {
                  return (
                    <Box component="ol" sx={{ m: 0, pl: 2.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {steps.map((item, idx) => {
                        const line =
                          typeof item.action === "string"
                            ? item.action
                            : (() => {
                                try {
                                  return JSON.stringify(item.action);
                                } catch (e) {
                                  console.warn("QueryChatPage: step action not serializable", e);
                                  return "";
                                }
                              })();
                        return (
                          <Typography key={idx} variant="body2" component="li" sx={{ mb: 0.5 }}>
                            <strong>Step {item.step}.</strong> {line}
                          </Typography>
                        );
                      })}
                    </Box>
                  );
                }
                return (
                  <Box
                    sx={{
                      "& p": { margin: 0 },
                      "& p + p": { mt: 0.75 },
                      "& ul, & ol": { margin: 0, paddingLeft: 2.5 },
                      "& li": { marginBottom: 0.25 },
                      "& h1:first-of-type, & h2:first-of-type, & h3:first-of-type": { mt: 0 },
                      wordBreak: "break-word",
                    }}
                  >
                    <ReactMarkdown components={chatMarkdownComponents}>{assistantText}</ReactMarkdown>
                  </Box>
                );
              })()}
              {isAssistant && ((sourcesLoading && i === messages.length - 1) || allRefs.length > 0) && (
                <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: "divider" }}>
                  {i === messages.length - 1 && sourcesLoading ? (
                    <Typography variant="caption" color="text.secondary">
                      Loading sources...
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Source documents
                      </Typography>
                      {msg.documentUsageMode === "supplement" && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontStyle: "italic" }}>
                          Authoritative / general medical knowledge may also inform this response.
                        </Typography>
                      )}
                      <ReferenceLink references={allRefs} listLayout onOpenPdf={handleOpenPdfRef} />
                      {assistantText &&
                        /cannot provide information|do not contain|none of these documents/i.test(assistantText) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, fontStyle: "italic" }}>
                          If your question topic isn&apos;t covered by the sources above, add a reference document in LLM Manager → this query → Document Settings → Embedded Documents.
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, alignSelf: "flex-start" }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Thinking...
            </Typography>
          </Box>
        )}
      </Paper>

      {sourcesLoading && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          Loading reference documents…
        </Typography>
      )}
      {/* Single PDF viewer modal for both inline source links and Source documents list */}
      <Dialog
        open={pdfViewer.open}
        onClose={handleClosePdfViewer}
        maxWidth={false}
        fullWidth
        sx={{ zIndex: 10100 }}
        PaperProps={{
          sx: {
            width: "90vw",
            height: "90vh",
            maxWidth: "1200px",
            m: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1,
            px: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <MenuBook color="primary" sx={{ fontSize: "1.2rem", flexShrink: 0 }} />
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
              {pdfViewer.title || "Document"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            {pdfViewer.url && (
              <Tooltip title="Open in new tab" arrow>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (pdfViewer.url) window.open(pdfViewer.url, "_blank", "noopener,noreferrer");
                  }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={handleClosePdfViewer}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {pdfViewer.url ? (
            <iframe
              src={`${pdfViewer.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
              title={pdfViewer.title || "PDF Viewer"}
              style={{ width: "100%", height: "100%", border: "none", flex: 1 }}
            />
          ) : (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">No document URL available.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Box sx={{ display: "flex", gap: 1, mt: compactLayout ? 0.5 : 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Type a message..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={loading}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={loading || !inputValue.trim()}
          aria-label="Send"
        >
          <SendIcon />
        </IconButton>
      </Box>
      {queryId === "ama-kijabe-1" && (
        <AiAssistantDisclaimer
          dense={compactLayout}
          sx={{ mt: compactLayout ? 1 : 1.5, px: compactLayout ? 0 : 0.5 }}
        />
      )}
      {compactLayout && (
        <Box sx={{ mt: 1, textAlign: "left" }}>
          <Typography
            variant="h6"
            component="span"
            sx={{ color: "primary.main", fontWeight: 600 }}
          >
            {publicRemaining !== null ? (
              <>
                {publicRemaining} unauthenticated uses remaining
              </>
            ) : (
              "\u2026"
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default QueryChatPage;
